/**
 * firebase.ts
 * GuroHub talks to TWO Firebase projects:
 *
 *  - DATA app  (gurohubpptapp): all GuroHub data — Firestore (classes/students),
 *    Realtime Database (live sessions), Storage (drawings/slide images), and
 *    anonymous student auth. This is the default app; getFirestoreDb/getRtdb/
 *    getStorageInstance/getAuthInstance all target it.
 *
 *  - AUTH app  (guroai-3932b): the main app's accounts + subscription tier.
 *    Used ONLY to authenticate the teacher and read users/{uid}.plan.
 *
 * A teacher signs into the AUTH app, then we exchange their ID token (via the
 * mintDataToken Cloud Function) for a DATA-app custom token carrying the SAME
 * uid + the tier as a custom claim. So the teacher's DATA session shares the
 * main-app UID, and data security rules can enforce ownership + tier.
 */

import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getFirestore, doc, getDoc, type Firestore } from 'firebase/firestore';
import { getDatabase, type Database } from 'firebase/database';
import { getStorage, type FirebaseStorage } from 'firebase/storage';
import {
  getAuth,
  signInAnonymously,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithCustomToken,
  signInWithCredential,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  setPersistence,
  indexedDBLocalPersistence,
  browserLocalPersistence,
  type Auth,
  type User,
} from 'firebase/auth';

// ---- Config (from .env) ------------------------------------

const dataConfig = {
  apiKey:            import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain:        import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId:         import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket:     import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId:             import.meta.env.VITE_FIREBASE_APP_ID,
  databaseURL:       import.meta.env.VITE_FIREBASE_DATABASE_URL,
};

const authConfig = {
  apiKey:            import.meta.env.VITE_AUTH_FIREBASE_API_KEY,
  authDomain:        import.meta.env.VITE_AUTH_FIREBASE_AUTH_DOMAIN,
  projectId:         import.meta.env.VITE_AUTH_FIREBASE_PROJECT_ID,
  storageBucket:     import.meta.env.VITE_AUTH_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_AUTH_FIREBASE_MESSAGING_SENDER_ID,
  appId:             import.meta.env.VITE_AUTH_FIREBASE_APP_ID,
};

const MINT_TOKEN_URL = import.meta.env.VITE_MINT_TOKEN_URL as string | undefined;

// ---- App singletons ----------------------------------------

let _dataApp: FirebaseApp | null = null;
let _authApp: FirebaseApp | null = null;
let _db: Firestore | null = null;
let _rtdb: Database | null = null;
let _storage: FirebaseStorage | null = null;
let _dataAuth: Auth | null = null;
let _teacherAuth: Auth | null = null;
let _authFirestore: Firestore | null = null;

function getDataApp(): FirebaseApp {
  if (!_dataApp) _dataApp = initializeApp(dataConfig);
  return _dataApp;
}

function getAuthApp(): FirebaseApp {
  if (!_authApp) _authApp = initializeApp(authConfig, 'authApp');
  return _authApp;
}

// ---- DATA project services (unchanged names) ---------------

export function getFirestoreDb(): Firestore {
  if (!_db) _db = getFirestore(getDataApp());
  return _db;
}

export function getRtdb(): Database {
  if (!_rtdb) _rtdb = getDatabase(getDataApp());
  return _rtdb;
}

export function getStorageInstance(): FirebaseStorage {
  if (!_storage) _storage = getStorage(getDataApp());
  return _storage;
}

/** Auth for the DATA app — students (anonymous) and the teacher's data session. */
export function getAuthInstance(): Auth {
  if (!_dataAuth) _dataAuth = getAuth(getDataApp());
  return _dataAuth;
}

// ---- AUTH project services ---------------------------------

/** Auth for the AUTH app (guroai-3932b) — teacher login. */
export function getTeacherAuth(): Auth {
  if (!_teacherAuth) _teacherAuth = getAuth(getAuthApp());
  return _teacherAuth;
}

/** Firestore for the AUTH app — reading users/{uid}.plan (tier). */
function getAuthFirestore(): Firestore {
  if (!_authFirestore) _authFirestore = getFirestore(getAuthApp());
  return _authFirestore;
}

// ---- Anonymous (student) auth on the DATA app --------------

let _currentUser: User | null = null;

/**
 * Signs a student in anonymously on the DATA app. Cached after first call.
 */
export async function ensureAnonymousAuth(): Promise<User> {
  if (_currentUser) return _currentUser;

  const auth = getAuthInstance();
  if (auth.currentUser) {
    _currentUser = auth.currentUser;
    return _currentUser;
  }

  const credential = await signInAnonymously(auth);
  _currentUser = credential.user;
  return _currentUser;
}

/**
 * Current DATA-app uid. For a teacher this is the main-app UID (via the custom
 * token); for a student it's the anonymous uid. Drives createClass(teacherId)
 * and submitAnswer(studentId).
 */
export function getCurrentUid(): string | null {
  return getAuthInstance().currentUser?.uid ?? _currentUser?.uid ?? null;
}

// ---- Teacher auth (AUTH app) + custom-token bridge ---------

async function ensurePersistence(auth: Auth): Promise<void> {
  try {
    await setPersistence(auth, indexedDBLocalPersistence);
    return;
  } catch {
    /* try next */
  }
  try {
    await setPersistence(auth, browserLocalPersistence);
  } catch {
    /* persistence unavailable; ignore */
  }
}

/**
 * Exchanges the teacher's AUTH-app identity for a DATA-app session with the
 * same UID (+ tier claim), via the mintDataToken Cloud Function. After this,
 * getCurrentUid() returns the main-app UID and data writes are authorized on
 * gurohubpptapp.
 */
async function bridgeToDataProject(teacher: User): Promise<void> {
  if (!MINT_TOKEN_URL) {
    console.error('[GuroHub] VITE_MINT_TOKEN_URL is not set — cannot bridge to the data project.');
    return;
  }
  const idToken = await teacher.getIdToken();
  const res = await fetch(MINT_TOKEN_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ idToken }),
  });
  if (!res.ok) {
    throw new Error(`mintDataToken failed: ${res.status} ${await res.text()}`);
  }
  const { token } = (await res.json()) as { token: string };
  const cred = await signInWithCustomToken(getAuthInstance(), token);
  _currentUser = cred.user;
}

/**
 * Ensures the DATA-project session exists before a data read/write. The data
 * session's persistence can be dropped after a task-pane reload in the Office
 * WebView; if it's gone but the teacher is still signed into the AUTH app,
 * re-bridge to restore it. Throws if no teacher is signed in at all.
 */
export async function ensureDataSession(): Promise<void> {
  if (getAuthInstance().currentUser) return;
  const teacher = getTeacherAuth().currentUser;
  if (!teacher) throw new Error('Not signed in. Please sign in again.');
  await bridgeToDataProject(teacher);
}

/** Creates a new teacher account on the AUTH app, then bridges to the data app. */
export async function signUpTeacher(email: string, password: string): Promise<User> {
  const auth = getTeacherAuth();
  await ensurePersistence(auth);
  const cred = await createUserWithEmailAndPassword(auth, email, password);
  await bridgeToDataProject(cred.user);
  return cred.user;
}

/** Signs an existing teacher in on the AUTH app, then bridges to the data app. */
export async function signInTeacher(email: string, password: string): Promise<User> {
  const auth = getTeacherAuth();
  await ensurePersistence(auth);
  const cred = await signInWithEmailAndPassword(auth, email, password);
  await bridgeToDataProject(cred.user);
  return cred.user;
}

/**
 * Signs the teacher in using a Google OAuth ID token obtained from the Office
 * auth dialog (see auth-dialog.ts), then bridges to the data project. Used
 * because popup/redirect Google sign-in is blocked inside the task pane WebView.
 */
export async function signInTeacherWithGoogleIdToken(googleIdToken: string): Promise<User> {
  const auth = getTeacherAuth();
  await ensurePersistence(auth);
  const cred = await signInWithCredential(auth, GoogleAuthProvider.credential(googleIdToken));
  await bridgeToDataProject(cred.user);
  return cred.user;
}

/** Signs the teacher out of BOTH apps. */
export async function signOutTeacher(): Promise<void> {
  await signOut(getTeacherAuth());
  await signOut(getAuthInstance()).catch(() => {});
  _currentUser = null;
}

/**
 * Subscribes to teacher auth state on the AUTH app. On restore (persisted
 * session) it re-bridges to the data app so data writes stay authorized.
 */
export function onTeacherAuthChanged(callback: (user: User | null) => void): () => void {
  return onAuthStateChanged(getTeacherAuth(), async (user) => {
    if (!user) {
      callback(null);
      return;
    }
    // Ensure the DATA session exists for this teacher (e.g. after a reload).
    if (getAuthInstance().currentUser?.uid !== user.uid) {
      try {
        await bridgeToDataProject(user);
      } catch (err) {
        console.error('[GuroHub] Re-bridge to data project failed:', err);
      }
    }
    callback(user);
  });
}

// ---- Subscription tier (AUTH app) --------------------------

export type Tier = 'free' | 'premium' | 'premium_plus';

/** Normalizes whatever string the main app stores into a known tier. */
export function normalizeTier(raw: unknown): Tier {
  const v = String(raw ?? '').toLowerCase().replace(/[\s-]+/g, '_');
  if (v === 'premium_plus' || v === 'premiumplus' || v === 'premium+') return 'premium_plus';
  if (v === 'premium' || v === 'pro' || v === 'paid') return 'premium';
  return 'free';
}

/**
 * Reads the signed-in teacher's tier from the AUTH project (users/{uid}.plan).
 * Defaults to 'free' if the doc/field is missing.
 */
export async function getUserTier(uid: string): Promise<Tier> {
  try {
    const snap = await getDoc(doc(getAuthFirestore(), 'users', uid));
    return normalizeTier(snap.exists() ? (snap.data() as { plan?: unknown }).plan : 'free');
  } catch (err) {
    console.warn('[GuroHub] Failed to read tier; defaulting to free:', err);
    return 'free';
  }
}
